/**
 * V2 admin — Traffic tab (V1 admin-traffic.js parity).
 *
 * page_view + navigation events from analytics_events, aggregated
 * client-side: daily trend (SVG area chart), page funnel, engagement
 * depth (bounced/browsed/explored/deep-dive), popular pages, top flows,
 * sources/mediums/devices, and the collapsible detail tables.
 */
import * as gateway from '../../core/gateway.js';
import { escapeHtml } from '../../../assets/js/utils.js';
import { fetchAll } from './data.js';
import { areaChart, CHART_COLORS } from './charts.js';

// ---------------------------------------------------------------------------
// Pure aggregations (exported for tests)
// ---------------------------------------------------------------------------

export function aggregateByField(rows, field) {
  const counts = {};
  rows.forEach((row) => {
    const val = row.event_data?.[field] || '(unknown)';
    counts[val] = (counts[val] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

export function sourceMediumBreakdown(rows) {
  const counts = {};
  rows.forEach((row) => {
    const key = `${row.event_data?.source || 'direct'} / ${row.event_data?.medium || 'none'}`;
    counts[key] = (counts[key] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

export function pageTypeFunnel(rows) {
  const counts = { splash: 0, app: 0, how_it_works: 0 };
  rows.forEach((row) => {
    const pt = row.event_data?.page_type;
    if (pt === 'splash') counts.splash++;
    else if (pt === 'how_it_works') counts.how_it_works++;
    else counts.app++; // legacy events had no page_type
  });
  return counts;
}

export function sessionStats(navRows) {
  const sessions = {};
  navRows.forEach((row) => {
    if (!row.session_id) return;
    (sessions[row.session_id] ||= []).push(row);
  });
  return Object.entries(sessions).map(([sid, events]) => ({
    sid,
    maxDepth: Math.max(...events.map((e) => e.event_data?.depth || 0)),
    eventCount: events.length,
  }));
}

export function depthDistribution(sessions) {
  const buckets = [
    { label: 'Bounced (1 page)', min: 0, max: 1, count: 0, color: CHART_COLORS.red },
    { label: 'Browsed (2–3)', min: 2, max: 3, count: 0, color: '#b45309' },
    { label: 'Explored (4–7)', min: 4, max: 7, count: 0, color: CHART_COLORS.blue },
    { label: 'Deep dive (8+)', min: 8, max: Infinity, count: 0, color: CHART_COLORS.green },
  ];
  sessions.forEach((s) => {
    const bucket = buckets.find((b) => s.maxDepth >= b.min && s.maxDepth <= b.max);
    if (bucket) bucket.count++;
  });
  return buckets;
}

export function topFlows(navRows, limit = 10) {
  const flows = {};
  navRows.forEach((row) => {
    const from = row.event_data?.from;
    const to = row.event_data?.view;
    if (!from || !to || from === to) return;
    const key = `${from} → ${to}`;
    flows[key] = (flows[key] || 0) + 1;
  });
  return Object.entries(flows)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

export function viewPopularity(navRows) {
  const counts = {};
  navRows.forEach((row) => {
    const view = row.event_data?.view;
    if (!view || view === 'admin') return;
    counts[view] = (counts[view] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

function dailyTrend(rows, days) {
  const perDay = {};
  const series = [];
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    series.push({
      date: dateStr,
      label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    });
    perDay[dateStr] = 0;
  }
  rows.forEach((row) => {
    const dateStr = row.created_at?.split('T')[0];
    if (perDay[dateStr] !== undefined) perDay[dateStr]++;
  });
  return series.map((d) => ({ ...d, count: perDay[d.date] || 0 }));
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

function barList(items, total) {
  const top = items.slice(0, 10);
  const max = top[0]?.count || 1;
  return `
    <div class="admin-bar-list">
      ${top.map((item) => `
        <div class="admin-bar-row">
          <span class="admin-bar-label">${escapeHtml(item.name)}</span>
          <span class="admin-bar-track"><span class="admin-bar-fill"
            style="width:${Math.max((item.count / max) * 100, 2)}%"></span></span>
          <span class="admin-bar-value">${item.count} <small>(${((item.count / total) * 100).toFixed(1)}%)</small></span>
        </div>`).join('')}
    </div>`;
}

function detailTable(title, rows, total, { open = false } = {}) {
  return `
    <div class="admin-card admin-card-span">
      <details ${open ? 'open' : ''}>
        <summary class="admin-card-title">${title}</summary>
        ${rows.length ? `
          <table class="admin-table">
            <thead><tr><th></th><th class="admin-num">Views</th>${total ? '<th class="admin-num">%</th>' : ''}</tr></thead>
            <tbody>
              ${rows.map((row) => `
                <tr>
                  <td>${escapeHtml(row.name)}</td>
                  <td class="admin-num"><strong>${row.count}</strong></td>
                  ${total ? `<td class="admin-num admin-cell-dim">${((row.count / total) * 100).toFixed(1)}%</td>` : ''}
                </tr>`).join('')}
            </tbody>
          </table>` : '<p class="admin-muted">No data yet.</p>'}
      </details>
    </div>`;
}

export function mount(el) {
  let destroyed = false;
  let dateRange = 30;
  let pageViews = [];
  let navRows = [];
  let loaded = false;

  async function load() {
    const since = new Date();
    since.setDate(since.getDate() - dateRange);
    const sinceISO = since.toISOString();
    [pageViews, navRows] = await Promise.all([
      fetchAll(gateway.TABLES.ANALYTICS_EVENTS, {
        select: 'event_data,created_at',
        filters: { event_name: 'eq.page_view', created_at: `gte.${sinceISO}` },
        order: 'created_at.desc',
      }),
      fetchAll(gateway.TABLES.ANALYTICS_EVENTS, {
        select: 'event_data,session_id,created_at',
        filters: { event_name: 'eq.navigation', created_at: `gte.${sinceISO}` },
        order: 'created_at.asc',
      }),
    ]);
    loaded = true;
  }

  function render() {
    if (!loaded) {
      el.innerHTML = '<div class="v2-loading">Counting footprints…</div>';
      return;
    }
    const total = pageViews.length;
    const funnel = pageTypeFunnel(pageViews);
    const sessions = sessionStats(navRows);
    const buckets = depthDistribution(sessions);
    const avgDepth = sessions.length
      ? (sessions.reduce((sum, s) => sum + s.maxDepth, 0) / sessions.length).toFixed(1) : '0';
    const trend = dailyTrend(pageViews, dateRange).slice(-14);
    const flows = topFlows(navRows);
    const views = viewPopularity(navRows);
    const sources = aggregateByField(pageViews, 'source');
    const mediums = aggregateByField(pageViews, 'medium');
    const devices = aggregateByField(pageViews, 'device_type');
    const referrers = aggregateByField(pageViews, 'referrer_domain').filter((r) => r.name !== '(unknown)');
    const campaigns = aggregateByField(pageViews, 'campaign').filter((r) => r.name !== '(unknown)');
    const landings = aggregateByField(pageViews, 'landing_page');

    el.innerHTML = `
      <div class="admin-card admin-card-span">
        <div class="admin-toolbar">
          <h3 class="admin-card-title">Traffic — <strong>${total.toLocaleString('en-US')}</strong> page views</h3>
          <span class="admin-toolbar-spacer"></span>
          <select class="admin-filter" data-traffic-range aria-label="Date range">
            ${[[7, 'Last 7 days'], [14, 'Last 14 days'], [30, 'Last 30 days'], [90, 'Last 90 days']]
              .map(([v, label]) => `<option value="${v}" ${dateRange === v ? 'selected' : ''}>${label}</option>`).join('')}
          </select>
        </div>
        ${areaChart(
          [{ values: trend.map((d) => d.count), color: CHART_COLORS.green, label: 'views' }],
          trend.map((d) => ({ label: d.label.split(' ')[1], date: d.date })),
        ) || '<p class="admin-muted">No views yet.</p>'}
        <p class="admin-axis-note">daily views, last 14 days</p>
      </div>

      <div class="admin-two-up">
        <div class="admin-card">
          <h3 class="admin-card-title">Page funnel</h3>
          <div class="admin-mini-stats">
            <div class="admin-mini-stat"><strong>${funnel.splash.toLocaleString('en-US')}</strong><span>splash page</span></div>
            <div class="admin-mini-stat"><strong>${funnel.app.toLocaleString('en-US')}</strong><span>the app</span></div>
            <div class="admin-mini-stat"><strong>${funnel.how_it_works.toLocaleString('en-US')}</strong><span>how it works</span></div>
          </div>
          ${funnel.splash > 0 ? `
            <p class="admin-muted">${((funnel.app / funnel.splash) * 100).toFixed(0)}% of splash visitors enter the app.</p>` : ''}
        </div>
        <div class="admin-card">
          <h3 class="admin-card-title">Engagement depth</h3>
          <div class="admin-mini-stats">
            <div class="admin-mini-stat"><strong>${sessions.length.toLocaleString('en-US')}</strong><span>sessions</span></div>
            <div class="admin-mini-stat"><strong>${avgDepth}</strong><span>avg pages deep</span></div>
            <div class="admin-mini-stat"><strong>${navRows.length.toLocaleString('en-US')}</strong><span>navigations</span></div>
          </div>
          ${sessions.length ? `
            <div class="admin-bar-list">
              ${buckets.map((b) => `
                <div class="admin-bar-row">
                  <span class="admin-bar-label">${b.label}</span>
                  <span class="admin-bar-track"><span class="admin-bar-fill"
                    style="width:${Math.max((b.count / sessions.length) * 100, 1)}%;background:${b.color}"></span></span>
                  <span class="admin-bar-value">${b.count} <small>(${((b.count / sessions.length) * 100).toFixed(0)}%)</small></span>
                </div>`).join('')}
            </div>` : ''}
        </div>
      </div>

      <div class="admin-boards">
        <div class="admin-card">
          <h3 class="admin-card-title">Pages visited</h3>
          ${views.length ? barList(views, navRows.length) : '<p class="admin-muted">No data yet.</p>'}
        </div>
        <div class="admin-card">
          <h3 class="admin-card-title">Top flows</h3>
          ${flows.length ? `
            <div class="admin-bar-list">
              ${flows.map((f) => `
                <div class="admin-bar-row">
                  <span class="admin-bar-label admin-bar-label-wide">${escapeHtml(f.name)}</span>
                  <span class="admin-bar-value">${f.count}</span>
                </div>`).join('')}
            </div>` : '<p class="admin-muted">No data yet.</p>'}
        </div>
        <div class="admin-card">
          <h3 class="admin-card-title">Devices</h3>
          ${devices.length ? barList(devices, total) : '<p class="admin-muted">No data yet.</p>'}
        </div>
      </div>

      <div class="admin-two-up">
        <div class="admin-card">
          <h3 class="admin-card-title">Sources</h3>
          ${sources.length ? barList(sources, total) : '<p class="admin-muted">No data yet.</p>'}
        </div>
        <div class="admin-card">
          <h3 class="admin-card-title">Medium</h3>
          ${mediums.length ? barList(mediums, total) : '<p class="admin-muted">No data yet.</p>'}
        </div>
      </div>

      ${detailTable('Source / medium detail', sourceMediumBreakdown(pageViews), total)}
      ${detailTable('Referrer domains', referrers, 0)}
      ${campaigns.length ? detailTable('UTM campaigns', campaigns, total) : ''}
      ${detailTable('Landing pages', landings, total)}`;
  }

  function onChange(event) {
    const range = event.target.closest('[data-traffic-range]');
    if (range) {
      dateRange = parseInt(range.value, 10);
      loaded = false;
      render();
      load().then(() => { if (!destroyed) render(); });
    }
  }

  render();
  load().then(() => { if (!destroyed) render(); })
    .catch(() => { if (!destroyed) el.innerHTML = '<div class="v2-error">Couldn’t load traffic data.</div>'; });

  el.addEventListener('change', onChange);

  return () => {
    destroyed = true;
    el.removeEventListener('change', onChange);
  };
}
